import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { scenarioQueryKeys } from "../queryKeys";
import { resourcefulScenarioRequest, unlinkScenarioAsset } from "../requests";

export function useAssetsQuery({
  scenarioID,
  initialData,
  queryKey,
  additionalInvalidationKeys = [],
}) {
  const queryClient = useQueryClient();
  // NOTE: We should contain a list of scenario resources
  // and reference assets from there
  const assetRequests = resourcefulScenarioRequest(scenarioID, "assets");

  const assetsQuery = useQuery({
    queryFn: assetRequests.index,
    queryKey,
    initialData,
  });

  const invalidateQueries = async () => {
    const keys = scenarioQueryKeys(scenarioID);
    await Promise.all([
      keys.assets, keys.forecasts, keys.assetAllocation, keys.riskProfile, keys.fna,
      ...additionalInvalidationKeys,
    ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
  };

  const updateAssetMutation = useMutation({
    mutationFn: assetRequests.update,
    onSuccess: invalidateQueries,
  });

  const createAssetMutation = useMutation({
    mutationFn: assetRequests.create,
    onSuccess: invalidateQueries,
  });

  const deleteAssetMutation = useMutation({
    mutationFn: assetRequests.delete,
    onSuccess: invalidateQueries,
  });

  const unlinkAssetMutation = useMutation({
    mutationFn: ({ assetID, mode }) => unlinkScenarioAsset(scenarioID, assetID, mode),
    onSuccess: async (unlinkedAsset) => {
      // Update the active list immediately. The refetch below keeps all
      // calculated associations in sync, but the action should disappear as
      // soon as the unlink request succeeds rather than after a page reload.
      const assetsKey = [scenarioQueryKeys(scenarioID).assets];
      queryClient.setQueryData(assetsKey, (currentAssets) =>
        Array.isArray(currentAssets)
          ? currentAssets.map((asset) =>
              asset.id === unlinkedAsset?.id
                ? { ...asset, ...unlinkedAsset }
                : asset
            )
          : currentAssets
      );
      await invalidateQueries();
    },
  });

  const isFetching = assetsQuery.isFetching || updateAssetMutation.isLoading || unlinkAssetMutation.isLoading;

  const isAnyLoading =
    assetsQuery.isFetching ||
    createAssetMutation.isLoading ||
    updateAssetMutation.isLoading ||
    deleteAssetMutation.isLoading ||
    unlinkAssetMutation.isLoading;

  return {
    data: assetsQuery.data,
    actions: {
      update: updateAssetMutation.mutateAsync,
      create: createAssetMutation.mutateAsync,
      delete: deleteAssetMutation.mutateAsync,
      unlink: unlinkAssetMutation.mutateAsync,
    },
    status: assetsQuery.status,

    /* Boolean flags indicating various states of status */
    isFetching: isFetching,
    isAnyLoading: isAnyLoading,
  };
}
